import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, makeStyles, Link, withTheme, Grid } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import { NotificationAlert } from '@shiftfinancial/design-system';
import CardSvg from '../../../Components/svg/CardSvg';
import PlaneSvg from '../../../Components/svg/PlaneSvg';
import MedicalCircleSvg from '../../../Components/svg/MedicalCircleSvg';
import { useDispatch } from 'react-redux';

import {
  ATTACHMENT_SUPPORTED_FORMATS,
  MAX_FILES_ID_VERIFY,
  MAX_FILE_SIZE_IN_MB_ID_VERIFY,
  DOCUMENT_STATUSES,
  ID_TYPES,
  NETWORK_ERROR,
  NETWORK_CONNECTION_ERROR_MESSAGE,
} from '../../../constants';
import FormikDropzoneAttachFiles from '../../core/formik/FormikDropzoneAttachFiles';
import registrationApi from '../../../api/registrationApi';
import { selectSfContactId } from '../../../selectors';
import { gtmUserJourneyEvent } from '../../../utils/gtmUtils';
import { GTM_EVENT_TYPE_USER_JOURNEY } from '../../../constants';
import CompositeAlert from '../../core/CompositeAlert';
import { updateErrorMessage } from '../idverification/actions';

const useStyles = makeStyles((theme) => ({
  dropZoneContainer: {
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(2),
    },
  },
  declaration: {
    paddingBottom: theme.spacing(2),
    maxWidth: theme.spacing(79),
  },
  bold: {
    fontWeight: 500,
  },
  ul: {
    paddingLeft: theme.spacing(3),
    marginTop: 0,
    marginBottom: 0,
  },
  smallColumn: {
    width: theme.spacing(3.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: theme.spacing(22.25),
    [theme.breakpoints.down('xs')]: {
      height: theme.spacing(3.75),
      paddingBottom: theme.spacing(2),
      justifyContent: 'left',
    },
  },
  icon: { fill: 'transparent' },
  text: { fontWeight: 500, color: theme.palette.primary.main },
  notificationIcon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  link: { textDecoration: 'underline' },
  smallerScreenText: {
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
    display: 'none',
  },
}));

const UploadId = ({
  values,
  touched,
  errors,
  setFieldValue,
  shadowRegisteredAccountId,
  userJourneyAccountType,
  isAMLPEPPassed,
  errorMessage,
}) => {
  const classes = useStyles();
  const uniqueId = uuidv4();
  const [idTypeDocumentRequests, setIdTypeDocumentRequests] = useState([]);
  const sfContactId = useSelector(selectSfContactId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (sfContactId) {
      registrationApi
        .getLatestDocumentsPerType(sfContactId, shadowRegisteredAccountId)
        .then((response) => {
          const { data } = response;
          const documentRequests = data
            ? data.map((request) => {
                return {
                  documentId: request.documentId,
                  documentTypeId: request.documentTypeId,
                  status: request.status,
                };
              })
            : [];

          if (documentRequests.length > 0) {
            setIdTypeDocumentRequests(documentRequests);
          }
        });
    }
  }, [sfContactId]);

  useEffect(() => {
    setFieldValue('isAMLPEPPassed', isAMLPEPPassed);
  }, [isAMLPEPPassed]);

  const identityInstructions = [
    isAMLPEPPassed
      ? 'Please upload a driver licence OR passport.'
      : 'Please upload a driver licence OR passport and a medicare card if available.',
    `Upload files in PDF, JPG or PNG format. Max file size ${MAX_FILE_SIZE_IN_MB_ID_VERIFY}MB.`,
  ];

  function createNewDocumentRequest(idType, gcAccountId) {
    let secondaryRelations;
    if (gcAccountId) {
      secondaryRelations = [
        {
          secondaryEntityId: gcAccountId,
          secondaryEntityType: 'GcAccount',
          secondaryEntityName: 'GC AccountId',
        },
      ];
    }
    const requestValues = {
      primaryRelationId: sfContactId,
      documentTypeId: idType.value,
      Status: DOCUMENT_STATUSES.Uploaded,
      secondaryRelations,
    };

    return registrationApi
      .createDocument(gcAccountId, requestValues)
      .then((response) => {
        const createNewDocumentRequest = {
          documentTypeId: idType.documentTypeId,
          documentId: response.data,
          status: DOCUMENT_STATUSES.Uploaded,
        };
        setIdTypeDocumentRequests([...idTypeDocumentRequests, createNewDocumentRequest]);
        return createNewDocumentRequest;
      })
      .catch((error) => {
        const networkError =
          error.message && error.message === NETWORK_ERROR && NETWORK_CONNECTION_ERROR_MESSAGE;
        dispatch(updateErrorMessage(networkError || 'Something went wrong'));
      });
  }

  function getDocumentRequest(idType, gcAccountId) {
    dispatch(updateErrorMessage(''));
    if (idType?.documentTypeId) {
      const existingRequest = idTypeDocumentRequests.find(
        (item) => item.documentTypeId === idType.documentTypeId,
      );
      return existingRequest
        ? Promise.resolve(existingRequest)
        : createNewDocumentRequest(idType, gcAccountId);
    }
  }

  function uploadFile(gcAccountId, fileGroupId, file, idType) {
    if (idType?.value) {
      return getDocumentRequest(idType, gcAccountId).then((documentRequest) => {
        return documentRequest?.documentId
          ? registrationApi
              .uploadDocumentFile(gcAccountId, documentRequest.documentId, file)
              .then((response) => {
                const { data } = response;
                if (data.statusCode === 'OK') {
                  const uploadFileResponse = {
                    documentFileId: data.data.documentFileId,
                    error: false,
                  };
                  if (documentRequest.status !== DOCUMENT_STATUSES.Approved) {
                    return uploadFileResponse;
                  }

                  return registrationApi
                    .updateDocumentStatus({
                      documentId: documentRequest.documentId,
                      status: DOCUMENT_STATUSES.Uploaded,
                    })
                    .then(() => {
                      return uploadFileResponse;
                    })
                    .catch(() => {
                      return uploadFileResponse;
                    });
                }

                addGTMlog(idType);

                return { documentFileId: '', error: true };
              })
              .catch((error) => {
                const networkError =
                  error.message &&
                  error.message === NETWORK_ERROR &&
                  NETWORK_CONNECTION_ERROR_MESSAGE;
                dispatch(updateErrorMessage(networkError || 'Something went wrong'));
              })
          : null;
      });
    }
    return null;
  }

  function addGTMlog(idType) {
    setFieldValue('isIdUploadFail', true);
    const gtmUserJourneyEventObject = {
      eventType: GTM_EVENT_TYPE_USER_JOURNEY.REGO_ID_UPLOAD_FAILED,
      accountType: userJourneyAccountType,
      documentType: idType?.value,
    };
    gtmUserJourneyEvent(gtmUserJourneyEventObject);
  }

  function deleteFile(gcAccountId, documentFileId) {
    if (documentFileId) {
      registrationApi.deleteDocumentFile(gcAccountId, documentFileId);
    }
  }

  const renderIdUploader = ({ idType, icon }) => {
    return (
      <Box className={classes.dropZoneContainer}>
        <Box pb={1}>
          <Typography variant="body2" className={classes.bold}>
            {idType.text}
          </Typography>
        </Box>
        <Box className={classes.smallerScreenText} pb={1}>
          <Typography variant="body2">{idType.uploadFileText}</Typography>
        </Box>
        <Box className={classes.dropzone}>
          <FormikDropzoneAttachFiles
            dataTestid={`uia-uploadId-${idType.value}`}
            name={`${idType.value}DocumentFiles`}
            values={values}
            errors={errors}
            touched={touched}
            accept={ATTACHMENT_SUPPORTED_FORMATS.toString()}
            fileGroupId={uniqueId}
            gcAccountId={shadowRegisteredAccountId}
            onFileUpload={(gcAccountId, fileGroupId, file) => {
              return uploadFile(gcAccountId, fileGroupId, file, idType);
            }}
            onFileDelete={deleteFile}
            maxFilesCount={MAX_FILES_ID_VERIFY}
            maxFileSizeInMb={MAX_FILE_SIZE_IN_MB_ID_VERIFY}
            showDownload={false}
            icon={icon}
            uploadFileText={idType.uploadFileText}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box mt={2}>
      <Box pb={3}>
        <Grid container spacing={2}>
          {errorMessage && (
            <Grid item xs={12}>
              <CompositeAlert variant="error" message={errorMessage} />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box pb={3}>
        <NotificationAlert appearance="info" title="We could not verify your identity">
          <ul className={classes.ul}>
            {identityInstructions.map((item) => (
              <li key={item} className={classes.li}>
                {item}
              </li>
            ))}
          </ul>
        </NotificationAlert>
      </Box>

      <Grid container columns={13}>
        <Grid item xs={12} sm>
          {renderIdUploader({
            idType: ID_TYPES.DRIVERSLICENCE,
            icon: <CardSvg className={classes.icon}></CardSvg>,
          })}
        </Grid>
        <Grid item className={classes.smallColumn}>
          <Box pt={2} pb={2}>
            <Typography align="left" variant="body2" className={classes.text}>
              or
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm>
          {renderIdUploader({
            idType: ID_TYPES.PASSPORT,
            icon: <PlaneSvg className={classes.icon} />,
          })}
        </Grid>
        {!isAMLPEPPassed && (
          <React.Fragment>
            <Grid item className={classes.smallColumn}>
              <Box pt={2} pb={2}>
                <Typography align="left" variant="h3" className={classes.text}>
                  +
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm>
              {renderIdUploader({
                idType: ID_TYPES.MEDICARE,
                icon: <MedicalCircleSvg className={classes.icon} />,
              })}
            </Grid>
          </React.Fragment>
        )}
      </Grid>

      <Box className={classes.declaration}>
        <Typography variant="h6" style={{ fontWeight: '400' }}>
          By continuing, I agree to have my identity information verified with the issuer or
          official record holder and I agree to Shift's{' '}
          <Link
            href="https://www.shift.com.au/privacy-policy"
            target="_blank"
            className={classes.link}
            data-testid="information-verified-link"
          >
            collection notice
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default withTheme(UploadId);
